import React, { useEffect, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import 'react-quill/dist/quill.snow.css';
import { CardWrapper, SectionTitle } from '../UI';
import * as C from '../../theme/colors.constants';
import { DashboardController } from '../../../controller/dashboardController';
import { MURAL_VAZIO } from '../../../components/UI/EditorTexto/editorTexto.utils';
import usePodeGerenciarDashboard from '../../hooks/usePodeGerenciarDashboard';
import EditarMuralDialog from './EditarMuralDialog';

/**
 * Mural de Avisos.
 *
 * Busca o HTML real do mural no mount, reaproveitando
 * `DashboardController.buscarInformacoesMural()` (já usado pelo `Dashboard`
 * real e pela `CalendarioCard` — try/catch embutido, retorna `null` em
 * falha de rede). O HTML vem do editor Quill do back-office, por isso
 * reaproveita a mesma folha de estilo (`ql-editor`) do `DashboardMural.jsx`
 * real para renderizar listas/negrito etc. corretamente.
 *
 * Estado vazio (marcador `'<p><br></p>'` do Quill, resposta `null` por erro
 * de rede, ou ainda carregando) mostra a mesma caixa `paleGrey` vazia que já
 * existia visualmente aqui — sem a imagem placeholder do `Dashboard` real,
 * pra preservar a identidade visual do protótipo (decisão registrada em
 * docs/architecture/plano-remocao-mocks-home.md, Etapa 6).
 *
 * dangerouslySetInnerHTML: XSS herdado do mesmo padrão já aceito no
 * `Dashboard` real — sanitização é responsabilidade do backend/ponto de
 * gravação do aviso, não deste componente.
 *
 * Botão de edição: mesmo critério de habilitação do `AtualizarMuralDashboard`
 * real (permissão 1401, `dashboardGerenciar`). O gate é apenas cosmético —
 * o endpoint real (`POST /core/dashboard/mural`) é quem valida a permissão
 * de fato. Ver docs/plano-mural-avisos-card-botao-edicao.md.
 */
export default function MuralAvisosCard() {
  const [informacoesMural, setInformacoesMural] = useState('');
  const [dialogAberto, setDialogAberto] = useState(false);

  useEffect(() => {
    let montado = true;
    async function buscarMural() {
      const response = await DashboardController.buscarInformacoesMural();
      if (montado) setInformacoesMural(response?.data ?? '');
    }
    buscarMural();
    return () => { montado = false; };
  }, []);

  const podeGerenciar = usePodeGerenciarDashboard();

  const temConteudo = Boolean(informacoesMural) && informacoesMural !== MURAL_VAZIO;

  function onMuralSalvo(novoValor) {
    setInformacoesMural(novoValor);
    setDialogAberto(false);
  }

  return (
    <CardWrapper>
      <SectionTitle
        right={(
          <Tooltip title={podeGerenciar ? 'Editar' : 'Você não tem permissão para editar'}>
            <span>
              <IconButton
                size="small"
                aria-label="Editar Mural de Avisos"
                disabled={!podeGerenciar}
                onClick={() => setDialogAberto(true)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      >
        Mural de Avisos
      </SectionTitle>
      <Box
        sx={{
          backgroundColor: C.paleGrey,
          borderRadius: 2,
          p: 2,
          minHeight: 'clamp(160px, 13vw, 260px)',
        }}
      >
        {temConteudo && (
          <div className="ql-editor" dangerouslySetInnerHTML={{ __html: informacoesMural }} />
        )}
      </Box>
      <EditarMuralDialog
        open={dialogAberto}
        valorAtual={informacoesMural}
        onClose={() => setDialogAberto(false)}
        onSalvo={onMuralSalvo}
      />
    </CardWrapper>
  );
}
